import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, FlaskConical, ScanLine, Watch, Sparkles, FileText, BellRing, ListChecks, History, LayoutDashboard, FilePlus, Video } from "lucide-react";
import AcompanhamentoTab from "@/pages/tabs/AcompanhamentoTab";
import LabTab from "@/pages/tabs/LabTab";
import ImagingTab from "@/pages/tabs/ImagingTab";
import WearablesTab from "@/pages/tabs/WearablesTab";
import InsightsTab from "@/pages/tabs/InsightsTab";
import ProntuarioTab from "@/pages/tabs/ProntuarioTab";
import AlertsTab from "@/pages/tabs/AlertsTab";
import PlanoTab from "@/pages/tabs/PlanoTab";
import HistoricoTab from "@/pages/tabs/HistoricoTab";
import DocumentosTab from "@/pages/tabs/DocumentosTab";
import TimelineTab from "@/pages/tabs/TimelineTab";
import PreConsultButton from "@/components/PreConsultButton";
import PortalAccessDialog from "@/components/PortalAccessDialog";
import PatientEditDialog from "@/components/PatientEditDialog";
import TeleconsultTab from "@/pages/tabs/TeleconsultTab";

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);

  const loadPatient = () => api.get(`/patients/${id}`).then((r) => setPatient(r.data)).catch(() => navigate("/pacientes"));
  useEffect(() => { loadPatient(); }, [id]);

  if (!patient) return null;

  return (
    <div className="p-6 sm:p-10 max-w-7xl">
      <Button variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground"
        onClick={() => navigate("/pacientes")} data-testid="back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> Pacientes
      </Button>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full overflow-hidden bg-primary/10 text-primary flex items-center justify-center font-heading font-bold text-xl shrink-0" data-testid="patient-avatar">
            {patient.photo
              ? <img src={patient.photo} alt={patient.name} className="w-full h-full object-cover" />
              : patient.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h1 className="font-heading text-3xl font-light tracking-tighter" data-testid="patient-name">{patient.name}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {patient.sex || "—"} · {patient.age ? `${patient.age} anos` : "idade n/d"}
              {patient.insurance ? ` · ${patient.insurance}` : ""}
              {patient.cpf ? ` · CPF ${patient.cpf}` : ""}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <PatientEditDialog patient={patient} onSaved={loadPatient} />
          <PreConsultButton patientId={id} />
          <PortalAccessDialog patient={patient} />
        </div>
      </div>

      <Tabs defaultValue="acompanhamento">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="acompanhamento" data-testid="tab-acompanhamento"><LayoutDashboard className="w-4 h-4 mr-2" /> Acompanhamento</TabsTrigger>
          <TabsTrigger value="prontuario" data-testid="tab-prontuario"><FileText className="w-4 h-4 mr-2" /> Prontuário Médico</TabsTrigger>
          <TabsTrigger value="lab" data-testid="tab-lab"><FlaskConical className="w-4 h-4 mr-2" /> Laboratório</TabsTrigger>
          <TabsTrigger value="imaging" data-testid="tab-imaging"><ScanLine className="w-4 h-4 mr-2" /> Imagens</TabsTrigger>
          <TabsTrigger value="wearables" data-testid="tab-wearables"><Watch className="w-4 h-4 mr-2" /> Wearables</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico"><History className="w-4 h-4 mr-2" /> Histórico</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts"><BellRing className="w-4 h-4 mr-2" /> Alertas</TabsTrigger>
          <TabsTrigger value="plano" data-testid="tab-plano"><ListChecks className="w-4 h-4 mr-2" /> Plano</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos"><FilePlus className="w-4 h-4 mr-2" /> Documentos</TabsTrigger>
          <TabsTrigger value="teleconsulta" data-testid="tab-teleconsulta"><Video className="w-4 h-4 mr-2" /> Teleconsulta</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline"><History className="w-4 h-4 mr-2" /> Linha do tempo</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights"><Sparkles className="w-4 h-4 mr-2" /> Análise IA</TabsTrigger>
        </TabsList>
        <TabsContent value="acompanhamento"><AcompanhamentoTab patientId={id} /></TabsContent>
        <TabsContent value="prontuario"><ProntuarioTab patientId={id} /></TabsContent>
        <TabsContent value="lab"><LabTab patientId={id} /></TabsContent>
        <TabsContent value="imaging"><ImagingTab patientId={id} /></TabsContent>
        <TabsContent value="wearables"><WearablesTab patientId={id} /></TabsContent>
        <TabsContent value="historico"><HistoricoTab patientId={id} /></TabsContent>
        <TabsContent value="alerts"><AlertsTab patientId={id} /></TabsContent>
        <TabsContent value="plano"><PlanoTab patientId={id} /></TabsContent>
        <TabsContent value="documentos"><DocumentosTab patientId={id} /></TabsContent>
        <TabsContent value="teleconsulta"><TeleconsultTab patientId={id} /></TabsContent>
        <TabsContent value="timeline"><TimelineTab patientId={id} /></TabsContent>
        <TabsContent value="insights"><InsightsTab patientId={id} /></TabsContent>
      </Tabs>
    </div>
  );
}
